import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import TabContainer from "ui/atoms/Container/TabContainer";
import { GameRound } from "shared/games/types/GameRound";
import useWindowIsBeingResized from "util/useWindowIsBeingResized";
import useOnChangeHandler from "../../molecules/GameRoundBuilders/useOnChangeHandler";
import BackToBasicOptionsHeader from "./BackToBasicOptionsHeader";
import AdvancedOptionsButtonDivider from "./AdvancedOptionsButtonDivider";
import DifficultySelectorAndStart from "./DifficultySelectorAndStart";
import AdvancedOptions from "./OptionsScreens/AdvancedOptionsScreen/AdvancedOptionsScreen";
import BasicOptions from "./OptionsScreens/BasicOptionsScreen";
import useScreensHeight from "./useScreensHeight";
import allTechniqueGameDescriptions, {
  descriptionsByGame,
} from "shared/games/allTechniqueGameDescriptions";
import * as routes from "routes";
import preloadGameRound from "shared/games/preloadGameRound";
import trackGameStart from "shared/analytics/trackGameStart";
import { instrumentsConfig } from "shared/games/instruments/instrumentsConfig";

interface Props {
  value: GameRound;
  onChange: (value: GameRound) => void;
  locked?: boolean;
}

export default function GameSelector({ onChange, value, locked }: Props) {
  const [advancedOptions, setAdvancedOptions] = useState(false);
  const windowIsResizing = useWindowIsBeingResized();
  const { advancedHeight, advancedRef, basicHeight, basicRef } =
    useScreensHeight();
  const changers = useOnChangeHandler({ onChange, value });

  return (
    <TabContainer
      locked={locked}
      title="Free Play"
      subtitle="Choose below what you want to practice"
      containerPadding="px-3 py-5"
      containerClassName="overflow-hidden"
    >
      <AnimatePresence mode="popLayout">
        {advancedOptions && (
          <div className="mb-6">
            <BackToBasicOptionsHeader
              onRequestBack={() => setAdvancedOptions(false)}
            />
          </div>
        )}
      </AnimatePresence>

      <div className="mb-7">
        <AnimatePresence initial={false}>
          <motion.div
            layout
            animate={advancedOptions ? "advanced" : "basic"}
            className={classNames(windowIsResizing && "overflow-hidden")}
            transition={{
              ease: "linear",
              duration: windowIsResizing ? 0 : 0.5,
              // delay: advancedOptions ? 0 : 1,
            }}
            initial={{ height: "auto" }}
            variants={{
              basic: {
                height: basicHeight,
              },
              advanced: {
                height: advancedHeight,
              },
            }}
          >
            {advancedOptions ? (
              <AdvancedOptions
                advancedRef={advancedRef}
                techniquesAndGames={allTechniqueGameDescriptions}
                instruments={instrumentsConfig}
                changers={changers}
                value={value}
              />
            ) : (
              <BasicOptions
                basicRef={basicRef}
                techniquesAndGames={allTechniqueGameDescriptions}
                changers={changers}
                value={value}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {!advancedOptions && (
        <AdvancedOptionsButtonDivider
          className="mb-7"
          onClick={() => setAdvancedOptions(true)}
        />
      )}

      <DifficultySelectorAndStart
        techniquesAndGames={allTechniqueGameDescriptions}
        onDifficultyChange={changers.onDifficultyChange}
        value={value}
        onLoadableClick={async () => {
          // If all instruments are selected, we don't need to send them since it's the default
          const allInstrumentsIncluded = Object.values(instrumentsConfig).every(
            ({ instrument }) => value.instruments.includes(instrument)
          );
          // If all games are selected, we don't need to send them since it's the default
          const allGamesIncluded = Object.values(descriptionsByGame).every(
            ({ game }) => value.games.includes(game)
          );

          const round: GameRound = {
            ...value,
            instruments: allInstrumentsIncluded ? [] : value.instruments,
            games: allGamesIncluded ? [] : value.games,
          };

          await preloadGameRound(value);

          trackGameStart({
            round,
            context: advancedOptions
              ? "mix-ear-training-selector//advanced"
              : "mix-ear-training-selector//basic",
          });

          return routes.earTraining.mixing.freePlayQueryParams({ ...round });
        }}
      />
    </TabContainer>
  );
}
